import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import {
    SITE_SECTION_DESCRIPTION_MAX_LENGTH,
    SITE_SECTION_DESCRIPTION_MIN_LENGTH,
    SITE_SECTION_TITLE_MAX_LENGTH,
    SITE_SECTION_TITLE_MIN_LENGTH,
} from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    FieldError,
    FieldGroup,
    Fieldset,
    Form,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { LocalizedTextField, useSiteStructureLocalization } from '../../Translations';
import { SiteStructureItemList } from './SiteStructureItemList';
import { TitleAndIconField } from './TitleAndIconField';

type ExternalLinkFormData = {
    title: string;
    localizedTitle: api.LocalizedString128;
    icon: string | null;
    description: string;
    localizedDescription: api.LocalizedString256;
    url: string;
};

export interface CreateSiteExternalLinkFormProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    parent?: api.SiteSectionGroup;
    onCreateExternalLink?: (siteExternalLink: api.SiteExternalLink) => void;
}

export function CreateSiteExternalLinkForm(props: CreateSiteExternalLinkFormProps) {
    const { organization, site, siteStructure, parent, onCreateExternalLink } = props;
    const form = useForm<ExternalLinkFormData>({
        defaultValues: {
            title: '',
            localizedTitle: {},
            icon: null,
            description: '',
            localizedDescription: {},
            url: '',
        },
    });
    const [isPending, startDialogAction] = useDialogAction();
    const localizationConfig = useSiteStructureLocalization(siteStructure);

    const onSubmit: SubmitHandler<ExternalLinkFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: siteExternalLink } =
                    await apiClient.siteExternalLinks.addExternalLinkToSite(
                        organization.id,
                        site.id,
                        {
                            title: data.title,
                            localizedTitle: data.localizedTitle,
                            description: data.description || null,
                            localizedDescription: data.localizedDescription,
                            icon: data.icon,
                            url: data.url,
                            draft: true,
                            ...(parent ? { parent: parent.id } : {}),
                        }
                    );
                onCreateExternalLink?.(siteExternalLink);
                toast.success('External link added');
                close();
            }, 'Error while adding external link');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset disabled={isPending}>
                    <TitleAndIconField
                        form={form}
                        titleName="title"
                        localizedTitleName="localizedTitle"
                        iconName="icon"
                        minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                        maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                        autoFocus
                        localization={localizationConfig}
                        titleInputFooter={
                            <div className="mt-1 text-ui-small text-muted">
                                <Icon.InfoCircle className="-mt-0.5 mr-1.5 inline-block shrink-0" />
                                {siteStructure.type === 'siteSpaces' ? (
                                    <>
                                        Adding an external link enables sections and moves your
                                        existing variants into a default section. External links
                                        start as drafts and aren't visible on your published site
                                        until undrafted.
                                    </>
                                ) : (
                                    <>
                                        External links start as drafts and aren't visible on your
                                        published site until undrafted.
                                    </>
                                )}
                            </div>
                        }
                    >
                        <Label>Title & Icon</Label>
                        <Description>
                            This title and icon will appear in your site's navigation.
                        </Description>
                    </TitleAndIconField>

                    <TextField form={form} name="url" required validate="http-url">
                        <Label>Destination URL</Label>
                        <Description>
                            Enter the full HTTP or HTTPS address visitors will open.
                        </Description>
                        <TextInput type="url" placeholder="https://example.com" />
                        <FieldError />
                    </TextField>

                    <FieldGroup>
                        <Label>Description</Label>
                        <Description>
                            Add context that can appear with the link in navigation.
                        </Description>
                        <LocalizedTextField
                            {...localizationConfig}
                            form={form}
                            name="description"
                            localizedName="localizedDescription"
                            inputProps={{
                                minLength: SITE_SECTION_DESCRIPTION_MIN_LENGTH,
                                maxLength: SITE_SECTION_DESCRIPTION_MAX_LENGTH,
                                placeholder: 'Enter description...',
                            }}
                        />
                    </FieldGroup>

                    {parent ? (
                        <SiteStructureItemList
                            description="The external link will be added to this group:"
                            items={[parent]}
                        />
                    ) : null}
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add external link" />
            </DialogFooter>
        </Form>
    );
}
